/**
 * Branding context object, types, defaults and hooks (white-label support).
 *
 * Split out of BrandingContext.tsx so that the .tsx module exports ONLY the
 * BrandingProvider component. React Fast Refresh (and the
 * `react-refresh/only-export-components` lint rule) requires a component module
 * not to also export hooks/constants; keeping those here keeps HMR working and
 * the lint signal clean.
 */
import { createContext, useContext } from "react";

export interface Branding {
  agency_name: string;
  agency_location: string;
  primary_color: string;
  accent_color: string;
  logo_url: string | null;
  favicon_url: string | null;
  /**
   * Purpose-built renditions the branding endpoint has always published but the
   * SPA never declared (#3417). `logo_compact_url` is the square/compact mark
   * for app chrome; `logo_letterhead_url` is the wide 900x240 lockup. Optional
   * because an env-only deployment (no uploaded `BrandingAsset`) has neither —
   * `utils/agencyLogo.ts` owns the fallback to `logo_url`.
   */
  logo_compact_url?: string | null;
  logo_letterhead_url?: string | null;
}

export interface BrandingContextType {
  branding: Branding;
  loading: boolean;
}

export const DEFAULT_BRANDING: Branding = {
  agency_name: "CRM",
  agency_location: "",
  primary_color: "#007AFF",
  accent_color: "#0066D6",
  logo_url: null,
  favicon_url: null,
  logo_compact_url: null,
  logo_letterhead_url: null,
};

export const BrandingContext = createContext<BrandingContextType>({
  branding: DEFAULT_BRANDING,
  loading: true,
});

export function useBranding(): Branding {
  return useContext(BrandingContext).branding;
}

export function useBrandingContext(): BrandingContextType {
  return useContext(BrandingContext);
}
